import { createSelector } from 'reselect'
import * as BookTypes from './Book.types'
import { sortBooksByCEFR } from './Book.utils'
import { AppState } from 'store/types'
import { flatten } from 'ramda'

export const selectLastUpdated = (state: AppState) => state.book.lastUpdated

export const selectBookStarted = (state: AppState) => state.book.bookStarted

export const selectProgressEntities = (state: AppState) => state.book.progressEntities

export const selectCurrentBookMode = (state: AppState) => state.book.currentBookMode

export const selectBooks = (state: AppState) => state.book.entities

export const selectPlacementTestEntity = (state: AppState) => state.book.placementTestEntity

export const selectPlacementTestScoreEntity = (state: AppState) => {
  const { placementTestEntity, placementTestScoreEntity } = state.book

  if (
    placementTestEntity !== null &&
    placementTestScoreEntity !== null &&
    placementTestEntity._id === placementTestScoreEntity.placementTestId
  ) {
    return placementTestScoreEntity
  }

  return null
}

export const selectCurrentBookEntity = createSelector(
  (state: AppState) => state.book.entities,
  (state: AppState) => state.book.currentBookId,
  (bookEntities, currentBookId) => {
    return bookEntities.find(({ _id }) => _id === currentBookId) || null
  }
)

export const selectCurrentBookProgressEntity = createSelector(
  (state: AppState) => state.book.currentBookId,
  (state: AppState) => state.book.progressEntities,
  (currentBookId, progressEntities) => {
    return progressEntities.find(({ bookId, isJourneyPage }) => bookId === currentBookId && !isJourneyPage) || null
  }
)

/** Returned `bookProgress` will be a journey book progress if `isCurrentBookJourney` is true */
export const selectCurrentBook = createSelector(
  selectCurrentBookEntity,
  selectCurrentBookProgressEntity,
  selectCurrentBookMode,
  (
    bookEntity,
    progressEntity,
    bookMode
  ): {
    book: BookTypes.Book | null
    bookProgress: BookTypes.BookProgress | null
    bookMode: BookTypes.BookMode | null
  } => ({
    book: bookEntity,
    bookProgress: progressEntity,
    bookMode
  })
)

export const selectLatestProgressEntity = createSelector(selectProgressEntities, (progressEntities) => {
  const unfinishedProgressEntities = progressEntities.filter((progressEntity) => !progressEntity.isCompleted)

  if (unfinishedProgressEntities.length > 0) {
    return unfinishedProgressEntities.reduce((progressEntity1, progressEntity2) =>
      progressEntity1.updatedAt > progressEntity2.updatedAt ? progressEntity1 : progressEntity2
    )
  }

  return null
})

export const selectReadingBooksWithProgress = createSelector(
  selectBooks,
  selectProgressEntities,
  (bookEntities, progressEntities): BookTypes.BookWithProgress[] => {
    const books = bookEntities
      .filter((book) => book.bookType === BookTypes.BookType.Reading || book.bookType === BookTypes.BookType.Mix)
      .map((bookEntity) => ({
        book: bookEntity,
        bookProgress:
          progressEntities.find(({ bookId, isJourneyPage }) => bookId === bookEntity._id && !isJourneyPage) || null
      }))

    return sortBooksByCEFR(books)
  }
)

export const selectListeningBooksWithProgress = createSelector(
  selectBooks,
  selectProgressEntities,
  (bookEntities, progressEntities): BookTypes.BookWithProgress[] => {
    const books = bookEntities
      .filter((book) => book.bookType === BookTypes.BookType.Listening || book.bookType === BookTypes.BookType.Mix)
      .map((bookEntity) => ({
        book: bookEntity,
        bookProgress:
          progressEntities.find(({ bookId, isJourneyPage }) => bookId === bookEntity._id && !isJourneyPage) || null
      }))
    return sortBooksByCEFR(books)
  }
)

export const selectFavouriteBooksWithProgress = createSelector(
  selectBooks,
  selectProgressEntities,
  (bookEntities, progressEntities): BookTypes.BookWithProgress[] => {
    const books = flatten(
      bookEntities
        .filter((book) => {
          const prog = progressEntities.find((p) => p.bookId === book._id)
          const isFavouriteListening = book.bookType === BookTypes.BookType.Listening && prog?.isFavorite?.listening
          const isFavouriteReading = book.bookType === BookTypes.BookType.Reading && prog?.isFavorite?.reading
          const isFavouriteMix =
            book.bookType === BookTypes.BookType.Mix && (prog?.isFavorite?.reading || prog?.isFavorite?.listening)

          return isFavouriteListening || isFavouriteReading || isFavouriteMix
        })
        .map((book) => {
          const bookProgress = progressEntities.find(({ bookId }) => bookId === book._id) || null

          if (book.bookType === BookTypes.BookType.Mix) {
            const outcome: BookTypes.BookWithProgress[] = []

            if (bookProgress?.isFavorite?.listening) {
              outcome.push({
                book: { ...book, bookType: BookTypes.BookType.Listening },
                bookProgress
              })
            }

            if (bookProgress?.isFavorite?.reading) {
              outcome.push({
                book: { ...book, bookType: BookTypes.BookType.Reading },
                bookProgress
              })
            }

            return outcome
          }

          return {
            book,
            bookProgress
          }
        })
    )
    return sortBooksByCEFR(books as BookTypes.BookWithProgress[])
  }
)

export const selectAllBooksWithProgress = createSelector(
  selectBooks,
  selectProgressEntities,
  (bookEntities, progressEntities) => {
    return bookEntities.map((bookEntity) => ({
      book: bookEntity,
      bookProgress: progressEntities.find(({ bookId }) => bookId === bookEntity._id) || null
    }))
  }
)

export const selectRecommendedBook = createSelector(
  selectBooks,
  selectProgressEntities,
  (bookEntities, progressEntities) => {
    const book = bookEntities.find((b) => b.recommended)

    if (book) {
      const bookProgress = progressEntities.find(({ bookId }) => bookId === book._id) || null

      return {
        book,
        bookProgress
      }
    }

    return null
  }
)

export const selectPlacementTest = (state: AppState) => state.book.placementTestEntity

export const selectReadingBooksByStatus = createSelector(
  selectReadingBooksWithProgress,
  selectRecommendedBook,
  (bookEntities, recommendedBookEntity) => {
    const completedEntities: BookTypes.BookWithProgress[] = bookEntities.filter(
      (book) => book.bookProgress?.isCompleted
    )

    return {
      completed: {
        entities: completedEntities,
        recommendedBookIndex: completedEntities.findIndex(
          ({ book }) =>
            book && recommendedBookEntity && recommendedBookEntity.book && book._id === recommendedBookEntity.book._id
        )
      },
      all: {
        entities: bookEntities,
        recommendedBookIndex: bookEntities.findIndex(
          ({ book }) =>
            book && recommendedBookEntity && recommendedBookEntity.book && book._id === recommendedBookEntity.book._id
        )
      }
    }
  }
)

export const selectListeningBooksByStatus = createSelector(
  selectListeningBooksWithProgress,
  selectRecommendedBook,
  (bookEntities, recommendedBookEntity) => {
    const completedEntities: BookTypes.BookWithProgress[] = bookEntities.filter(
      (book) => !!book.bookProgress?.isCompletedListening
    )

    return {
      completed: {
        entities: completedEntities,
        recommendedBookIndex: completedEntities.findIndex(
          ({ book }) =>
            book && recommendedBookEntity && recommendedBookEntity.book && book._id === recommendedBookEntity.book._id
        )
      },
      all: {
        entities: bookEntities,
        recommendedBookIndex: bookEntities.findIndex(
          ({ book }) =>
            book && recommendedBookEntity && recommendedBookEntity.book && book._id === recommendedBookEntity.book._id
        )
      }
    }
  }
)

export const selectHomeBooks = createSelector(
  selectRecommendedBook,
  selectListeningBooksByStatus,
  selectReadingBooksByStatus,
  selectFavouriteBooksWithProgress,
  (recommendedBookEntity, listeningBooks, readingBooks, favouriteBookEntities) => {
    return {
      recommendedBookEntity,
      listeningBooks,
      readingBooks,
      favoriteBooks: { entities: favouriteBookEntities, recommendedBookIndex: -1 }
    }
  }
)
